"use client";

import {
  Audio,
  TelnyxRTCContext,
  useCallbacks,
  useNotification,
} from "@telnyx/react-client";
import Call from "@telnyx/webrtc/lib/src/Modules/Verto/webrtc/Call";
import { useContext, useEffect, useState, type FC } from "react";
import { useDispatch, useSelector } from "react-redux";

import { type stateProps } from "../@types/types";
import { useIsOnline } from "../hooks/useIsOnline";
import {
  setDtmlNumber,
  setMakeCallData,
} from "../store/features/dialer/activeCollapseSlice";
import {
  setCallStatus,
  setOngoingCallTime,
  setSdkClientStatus,
} from "../store/features/dialer/callStatusSlice";
import {
  setAcceptIncomingCallStatus,
  setActiveCallData,
  setCallInfoData,
  setCallState,
  setHangupCallStatus,
  setHoldUnholdCallStatus,
  setIsIncoming,
  setIsOnCall,
  setMuteUnmuteCallStatus,
} from "../store/features/dialer/deviceSlice";
import { type RootState } from "../store/store";
import { CALL_DIRECTION } from "../utils/constants";

export interface CallInfo {
  dialcode: string;
  number: string;
  callDirection: string;
  sdk: string;
  my_phone_number: string;
}
interface TelnyxReactSDKProps {
  callInfo: CallInfo;
  setCallInfo: (callInfo: CallInfo | null) => void;
}

const TelnyxReactSDK = ({ callInfo, setCallInfo }: TelnyxReactSDKProps) => {
  const dispatch = useDispatch();
  const { isInternetAvailable } = useIsOnline();
  const {
    hangupCallStatus,
    acceptIncomingCallStatus,
    muteUnmuteCallStatus,
    holdUnholdCallStatus,
  } = useSelector((state: RootState) => state?.device);

  const device = useContext(TelnyxRTCContext);
  const notification = useNotification();
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const telnyxCallControlId =
    activeCall && activeCall?.options?.telnyxCallControlId;
  const { profile } = useSelector((state: stateProps) => state.global);
  const call = notification?.call;
  useCallbacks({
    onReady: () => {
      console.log("client ready");
      setDeviceConnected(true);
      dispatch(setSdkClientStatus(true));
    },
    onError: (e) => {
      console.log("client registration error", e);
      dispatch(setSdkClientStatus(false));
      device?.connect();
    },
    // onSocketError: () => {
    //   console.log('client socket error');
    //   dispatch(setSdkClientStatus(false));
    //   device?.disconnect();
    // },
    onSocketClose: () => {
      console.log("Client disconnected");
      dispatch(setSdkClientStatus(false));
      device?.connect();
    },
    onNotification: (x) => console.log("received notification:", x),
  });

  useEffect(() => {
    dispatch(
      setCallInfoData({
        ...callInfo,
        // number: callInfo?.dialcode + callInfo?.number,
        number: callInfo?.number,
      })
    );
    if (device) {
      device.connect();
    }
  }, [device, callInfo]);
  useEffect(() => {
    dispatch(setCallInfoData(callInfo));
    if (deviceConnected) {
      if (
        callInfo &&
        callInfo?.number &&
        callInfo?.callDirection === CALL_DIRECTION.OUTBOUND &&
        callInfo?.sdk === "telnyx"
      ) {
        const timer = setTimeout(createOutgoingCall, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [callInfo?.callDirection == CALL_DIRECTION.OUTBOUND, deviceConnected]);

  useEffect(() => {
    if (
      (call?.state === "ringing" && !activeCall) ||
      (call &&
        activeCall &&
        call?.options?.remoteCallerNumber ===
          activeCall?.options?.remoteCallerNumber) // To check if the call update is from same caller
    ) {
      setActiveCall(call);
    }
  }, [call, activeCall]);

  useEffect(() => {
    dispatch(setCallState(activeCall?.state));
    if (deviceConnected) {
      if (activeCall?.state == "active") {
        dispatch(setIsOnCall(true));
      }

      if (activeCall?.state === "ringing") {
        incomingCall();
      }
      if (activeCall && activeCall?.state == "hangup") {
        // activeCall?.direction !== 'outbound' && activeCall?.hangup();

        hangupCall();
      }
      if (activeCall && activeCall?.state == "destroy") {
        dispatch(setHoldUnholdCallStatus(false));
        hangupCall();
      }
    }
  }, [activeCall?.state]);

  useEffect(() => {
    const handleUnload = () => {
      activeCall && hangupCall();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const incomingCall = () => {
    dispatch(setCallStatus(true));
    dispatch(setCallState(activeCall?.state));
    dispatch(setIsIncoming(true));
    const activeCallData = {
      from: "+" + activeCall?.options?.remoteCallerNumber,
    };
    dispatch(setActiveCallData(activeCallData));
    setCallInfo({
      ...callInfo,
      ...activeCallData,
      callDirection: CALL_DIRECTION.INCOMING,
    });
  };

  const acceptIncomingCall = () => {
    console.log("Accepted incoming call");
    if (
      notification?.type === "callUpdate" &&
      activeCall?.state === "ringing"
    ) {
      activeCall?.answer(telnyxCallControlId as any);
      dispatch(setIsOnCall(true));
    }
  };

  const hangupCall = () => {
    setActiveCall(null);
    dispatch(setMakeCallData(null));
    dispatch(setIsOnCall(false));
    dispatch(setCallStatus(false));
    dispatch(setIsIncoming(false));
    setCallInfo(null);

    // Reset alloki
    dispatch(setCallState(null));
    dispatch(setCallInfoData(null));
    dispatch(setAcceptIncomingCallStatus(false));
    dispatch(setHangupCallStatus(false));
    dispatch(setOngoingCallTime("reset"));
    dispatch(setDtmlNumber(""));
    dispatch(setIsOnCall(false));
    dispatch(setHoldUnholdCallStatus(false));
    dispatch(setMuteUnmuteCallStatus(false));
    window.close();
  };

  const muteUnmuteCall = (isMute: boolean) => {
    if (activeCall) {
      isMute ? activeCall.muteAudio() : activeCall.unmuteAudio();
    }
  };

  const holdUnholdCall = (isOnHold: boolean) => {
    if (activeCall) {
      isOnHold ? activeCall.hold() : activeCall.unhold();
    }
  };

  const createOutgoingCall = () => {
    dispatch(setCallStatus(true));
    try {
      const call = device?.newCall({
        destinationNumber: `${callInfo?.dialcode}${callInfo?.number}`,
        callerName: callInfo?.my_phone_number || "",
        callerNumber: callInfo?.my_phone_number || "",
        audio: true,
        video: false,
        customHeaders: [{ name: "X-Header", value: profile?.id }],
      });
      setActiveCall(call!);

      // setIsOnCall(true);
      dispatch(setIsOnCall(true));
      console.log("Outgoing Call Created");
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (hangupCallStatus) {
      activeCall?.hangup();
      hangupCall();
    }
  }, [hangupCallStatus, activeCall]);
  useEffect(() => {
    if (!isInternetAvailable) {
      hangupCall();
    }
  }, [isInternetAvailable]);
  useEffect(() => {
    muteUnmuteCall(muteUnmuteCallStatus);
  }, [muteUnmuteCallStatus, activeCall]);

  useEffect(() => {
    holdUnholdCall(holdUnholdCallStatus);
  }, [holdUnholdCallStatus]);

  useEffect(() => {
    acceptIncomingCallStatus && acceptIncomingCall();
  }, [acceptIncomingCallStatus]);

  return <Audio stream={activeCall?.remoteStream} />;
};

export default TelnyxReactSDK;
